import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PropertyCard = ({ property, onSave, onCompare, isComparing, isSaved }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === property.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? property.images.length - 1 : prev - 1
    );
  };

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSave(property.id);
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCompare(property.id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-success text-success-foreground';
      case 'few_left': return 'bg-warning text-warning-foreground';
      case 'sold_out': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <div className="flex items-center space-x-1 bg-success/10 text-success px-2 py-1 rounded-full">
        <Icon name="CheckCircle" size={12} />
        <span className="text-xs font-medium">Verified</span>
      </div>
    ) : (
      <div className="flex items-center space-x-1 bg-warning/10 text-warning px-2 py-1 rounded-full">
        <Icon name="AlertCircle" size={12} />
        <span className="text-xs font-medium">Unverified</span>
      </div>
    );
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-property-card hover:shadow-modal transition-smooth overflow-hidden">
      {/* Image Gallery */}
      <div className="relative h-48 overflow-hidden">
        <Link to={`/property-detail-page?id=${property.id}`}>
          <Image
            src={property.images[currentImageIndex]}
            alt={property.title}
            className="w-full h-full object-cover hover:scale-105 transition-smooth"
          />
        </Link>
        
        {/* Image Navigation */}
        {property.images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 transition-quick"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70 transition-quick"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </>
        )}

        {/* Image Indicators */}
        {property.images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
            {property.images.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(property.status)}`}>
            {property.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="absolute top-2 right-2 flex space-x-1">
          <button
            onClick={handleSave}
            className={`p-2 rounded-full transition-quick ${
              isSaved 
                ? 'bg-error text-error-foreground' 
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
          >
            <Icon name="Heart" size={16} />
          </button>
          <button
            onClick={handleCompare}
            className={`p-2 rounded-full transition-quick ${
              isComparing 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-black/50 text-white hover:bg-black/70'
            }`}
          >
            <Icon name="GitCompare" size={16} />
          </button>
        </div>

        {/* New Listing Badge */}
        {property.isNew && (
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
            <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
              NEW
            </span>
          </div>
        )}
      </div>

      {/* Property Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <Link to={`/property-detail-page?id=${property.id}`}>
              <h3 className="font-semibold text-text-primary hover:text-primary transition-quick line-clamp-1">
                {property.title}
              </h3>
            </Link>
            <p className="text-sm text-text-secondary mt-1 flex items-center">
              <Icon name="MapPin" size={14} className="mr-1" />
              {property.location}
            </p>
          </div>
          {getVerificationBadge(property.ownerVerified)}
        </div>

        {/* Price */}
        <div className="mb-3">
          <div className="flex items-baseline space-x-2">
            <span className="text-2xl font-bold text-primary">₹{property.price.toLocaleString()}</span>
            <span className="text-sm text-text-secondary">/month</span>
          </div>
          {property.originalPrice && property.originalPrice > property.price && (
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-text-secondary line-through">₹{property.originalPrice.toLocaleString()}</span>
              <span className="text-xs bg-success/10 text-success px-2 py-1 rounded-full">
                {Math.round(((property.originalPrice - property.price) / property.originalPrice) * 100)}% OFF
              </span>
            </div>
          )}
        </div>

        {/* Property Specs */}
        <div className="flex items-center space-x-4 mb-3 text-sm text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="Bed" size={14} />
            <span>{property.bedrooms} Bed</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Bath" size={14} />
            <span>{property.bathrooms} Bath</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Square" size={14} />
            <span>{property.area} sqft</span>
          </div>
        </div>

        {/* Amenities */}
        <div className="flex flex-wrap gap-1 mb-3">
          {property.amenities.slice(0, 3).map((amenity, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
            >
              {amenity}
            </span>
          ))}
          {property.amenities.length > 3 && (
            <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
              +{property.amenities.length - 3} more
            </span>
          )}
        </div>

        {/* Owner Info removed as per requirements */}

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Phone"
            iconPosition="left"
          >
            Call
          </Button>
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="MessageCircle"
            iconPosition="left"
          >
            Inquire
          </Button>
        </div>

        {/* Quick Stats removed as per requirements */}
      </div>
    </div>
  );
};

export default PropertyCard;